import {
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  useMutation,
  keepPreviousData,
} from '@tanstack/vue-query'
import type { PaginationState, SortingState } from '@tanstack/vue-table'
import type { Schemas } from '@@/api/types.js'
import type { Option } from '@/components/base/Combobox.vue'
import {
  API_USERS_DETAIL_URL,
  API_USERS_STAFF_URL,
  API_USERS_URL,
  USER_PERMISSIONS_UPDATE_URL,
  USER_PERMISSIONS_URL,
  type CreateUser,
  type UpdateUser,
  type UsersListQueryParams,
  type UsersListResponse,
  type UsersStaffListQueryParams,
  type UsersStaffListResponse,
  type UsersUpdateResponse,
} from '@/models/users.js'
import { useApi } from '@/composables/useApi.js'
import { useInfiniteScrollPaginator } from '@/composables/useInfiniteScrollPaginator.js'
import {
  createRelationshipBuilder,
  many,
  one,
  toPaginationQueryParams,
  type RelationshipDefs,
} from '@/utils/jsonapi.js'
import { sortingStateToQueryParam } from '@/utils/sorting.js'
import { getUserFullName } from '@/utils/user.js'
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (query: Ref<UsersListQueryParams>) =>
    [...userKeys.lists(), query] as const,
  listStaff: (query: Ref<UsersStaffListQueryParams>) =>
    [...userKeys.lists(), 'staff', query] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
}

export function useUserListQuery(
  pagination: MaybeRef<PaginationState>,
  sorting: MaybeRef<SortingState>,
  filters?: {
    search: MaybeRef<string>
    isActive?: MaybeRef<boolean>
  },
) {
  const api = useApi()
  const route = useRoute()

  // @ts-expect-error - 'sort' should eventually be an array, awaiting a fix in the API
  const queryParams = computed<UsersListQueryParams>(() => ({
    ...toPaginationQueryParams(pagination),
    sort: sortingStateToQueryParam(unref(sorting)),
    'filter[search]': unref(filters?.search) || undefined,
    'filter[is_active]': unref(filters?.isActive),
    // TODO: reinstate once the real API supports it
    // include: 'permission_group',
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: userKeys.list(queryParams),
    queryFn: () =>
      api((client) =>
        client.GET(API_USERS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: unref(queryParams),
          },
        }),
      ),
  })

  // to ensure types can be narrowed correctly,
  // we need to return the query as a reactive object
  // see: https://github.com/TanStack/query/issues/5065#issuecomment-1458639556
  return reactive(query)
}

type SelectFunctionType<TData> = (response: UsersStaffListResponse) => TData
type UserStaffOptions = Omit<UsersStaffListResponse, 'data'> & {
  data: Option[]
}
const defaultSelect = (response: UsersStaffListResponse): UserStaffOptions => ({
  ...response,
  data: response.data.map(({ id, attributes }) => ({
    value: id,
    label: getUserFullName(attributes),
  })),
})

export function useUserStaffListQuery<TQueryResponse = UserStaffOptions>({
  sorting,
  pagination,
  filters,
  enabled = true,
  select: customSelect,
}: {
  sorting: MaybeRef<SortingState>
  pagination: MaybeRef<PaginationState>
  enabled?: MaybeRef<boolean>
  filters: {
    role: MaybeRef<UsersStaffListQueryParams['filter[role]']>
    departmentId?: MaybeRef<string>
    search?: MaybeRef<string | undefined>
  }
  select?: SelectFunctionType<TQueryResponse>
}) {
  const api = useApi()
  const route = useRoute()

  // @ts-expect-error - 'sort' should eventually be an array, awaiting a fix in the API
  const queryParams = computed<UsersStaffListQueryParams>(() => ({
    ...toPaginationQueryParams(pagination),
    sort: sortingStateToQueryParam(unref(sorting)),
    'filter[role]': unref(filters?.role)?.toLowerCase() || undefined,
    'filter[search]': unref(filters?.search) || undefined,
    'filter[department.id]': unref(filters?.departmentId) || undefined,
  }))

  const selectFn =
    customSelect || (defaultSelect as SelectFunctionType<TQueryResponse>)

  const query = useQuery({
    placeholderData: keepPreviousData,
    enabled,
    queryKey: userKeys.listStaff(queryParams),
    queryFn: () =>
      api((client) =>
        client.GET(API_USERS_STAFF_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: unref(queryParams),
          },
        }),
      ),
    select: (response): TQueryResponse => selectFn(response),
  })

  // to ensure types can be narrowed correctly,
  // we need to return the query as a reactive object
  // see: https://github.com/TanStack/query/issues/5065#issuecomment-1458639556
  return reactive(query)
}

export function useUserInfiniteListQuery(
  paginationState: Ref<PaginationState>,
  sorting: Ref<SortingState>,
  filters?: {
    search: MaybeRef<string>
  },
) {
  const api = useApi()
  const route = useRoute()
  const { initialPageParam, getNextPageParam, enablePaginationWatchers } =
    useInfiniteScrollPaginator<UsersListResponse>(paginationState)

  // @ts-expect-error - 'sort' should eventually be an array, awaiting a fix in the API
  const queryParams = computed<Partial<UsersListQueryParams>>(() => ({
    sort: sortingStateToQueryParam(unref(sorting)),
    'filter[search]': unref(filters?.search) || undefined,
  }))

  const query = useInfiniteQuery({
    placeholderData: keepPreviousData,
    queryKey: userKeys.list(queryParams),
    queryFn: async ({
      pageParam = {
        pageIndex: paginationState.value.pageIndex,
        pageSize: paginationState.value.pageSize,
      },
    }) =>
      await api((client) =>
        client.GET(API_USERS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: {
              ...queryParams.value,
              'page[number]': pageParam.pageIndex,
              'page[size]': pageParam.pageSize,
            },
          },
        }),
      ),

    initialPageParam,
    getNextPageParam,
  })

  enablePaginationWatchers(query)

  return reactive(query)
}

export function useUserStaffInfiniteListQuery(
  paginationState: Ref<PaginationState>,
  sorting: Ref<SortingState>,
  filters: {
    role: MaybeRef<UsersStaffListQueryParams['filter[role]']>
    departmentId?: MaybeRef<string>
    search?: MaybeRef<string | undefined>
  },
) {
  const api = useApi()
  const route = useRoute()
  const { initialPageParam, getNextPageParam, enablePaginationWatchers } =
    useInfiniteScrollPaginator<UsersStaffListResponse>(paginationState)

  // @ts-expect-error - role filter can be undefined but types expect it to be defined
  const queryParams = computed<Partial<UsersStaffListQueryParams>>(() => ({
    sort: sortingStateToQueryParam(unref(sorting)),
    'filter[role]': unref(filters?.role)?.toLowerCase() || undefined,
    'filter[search]': unref(filters?.search) || undefined,
    'filter[department.id]': unref(filters?.departmentId) || undefined,
  }))

  const query = useInfiniteQuery({
    placeholderData: keepPreviousData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryKey: userKeys.listStaff(queryParams as any),
    queryFn: async ({
      pageParam = {
        pageIndex: paginationState.value.pageIndex,
        pageSize: paginationState.value.pageSize,
      },
    }) =>
      await api((client) =>
        client.GET(API_USERS_STAFF_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: {
              ...queryParams.value,
              'page[number]': pageParam.pageIndex,
              'page[size]': pageParam.pageSize,
            } as UsersStaffListQueryParams,
          },
        }),
      ),

    initialPageParam,
    getNextPageParam,
  })

  enablePaginationWatchers(query)

  return reactive(query)
}

export function useUsersByIdsQuery(ids: MaybeRef<string[]>) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const query = useQuery({
    // Sort IDs so the cache key is stable regardless of selection order
    queryKey: computed(() => [
      ...userKeys.all,
      'byIds',
      unref(ids).slice().sort().join(','),
    ]),
    enabled: computed(() => unref(ids).length > 0),
    queryFn: () =>
      api((client) =>
        client.GET(API_USERS_URL, {
          params: {
            path: { provet_id: provetId.value },
            query: { 'filter[id.in]': unref(ids).join(',') },
          },
        }),
      ),
  })

  return reactive(query)
}

export function useUserDetailQuery(userId: string) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () =>
      api((client) =>
        client.GET(API_USERS_DETAIL_URL, {
          params: {
            path: { provet_id: route.params.provetId, id: userId },
          },
        }),
      ),
  })

  return reactive(query)
}

export function useUserCreateMutation() {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({ permission_group_id, ...user }: CreateUser) =>
      api((client) =>
        client.POST(API_USERS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: {
            data: {
              type: 'User',
              attributes: user,
              relationships: {
                permission_group: {
                  data: {
                    type: 'PermissionGroup',
                    id: permission_group_id,
                  },
                },
              },
            },
          },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
  })
}

export function useUserUpdateMutation(id: string) {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({ permission_group_id, ...user }: UpdateUser) =>
      api((client) =>
        client.PATCH(API_USERS_DETAIL_URL, {
          params: {
            path: { provet_id: route.params.provetId, id },
          },
          body: {
            data: {
              type: 'User',
              id,
              attributes: user,
              relationships: {
                permission_group: {
                  data: {
                    type: 'PermissionGroup',
                    id: permission_group_id,
                  },
                },
              },
            },
          },
        }),
      ),
    onSuccess: async (response: UsersUpdateResponse) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      queryClient.setQueryData(userKeys.detail(id), response)
    },
  })
}

export function useUserPermissionsQuery(
  userId: MaybeRef<string>,
  departmentId: MaybeRef<string>,
  enabled: MaybeRef<boolean>,
) {
  const api = useApi()
  const route = useRoute()

  return useQuery({
    enabled,
    queryKey: ['userPermissions', unref(userId), unref(departmentId)],
    queryFn: () => {
      return api((client) =>
        client.GET(USER_PERMISSIONS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: unref(userId),
            },
            query: {
              'filter[department_id]': Number(unref(departmentId)),
            },
          },
        }),
      )
    },
  })
}

export function useUserPermissionsMutation(
  departmentId: MaybeRef<string | undefined>,
) {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      userId,
      permissionGroupId,
    }: {
      userId: string
      permissionGroupId: string | number
    }) => {
      const depId = unref(departmentId)
      if (!depId) {
        throw new Error('Department ID is required')
      }
      return api((client) =>
        client.POST(USER_PERMISSIONS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: userId,
            },
          },
          body: {
            data: {
              type: 'UserDepartmentPermission',
              relationships: {
                permission_group: {
                  data: {
                    type: 'PermissionGroup',
                    id: permissionGroupId.toString(),
                  },
                },
                department: {
                  data: { type: 'Department', id: depId },
                },
              },
            },
          },
        }),
      )
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['locumUser', variables.userId],
      })
      await queryClient.invalidateQueries({ queryKey: ['locum-users-list'] })
      await queryClient.invalidateQueries({
        queryKey: ['clinic-staff-list', route.params.provetId],
      })
      await queryClient.invalidateQueries({
        queryKey: ['clinicStaffUsers'],
      })
    },
  })
}

export function useUserPermissionsUpdateMutation(
  departmentId: MaybeRef<string | undefined>,
) {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      userId,
      permissionSettingId,
      newPermissionId,
    }: {
      userId: string
      permissionSettingId: string
      newPermissionId: string
    }) => {
      const depId = unref(departmentId)
      if (!depId) {
        throw new Error('Department ID is required')
      }
      return api((client) =>
        client.PATCH(USER_PERMISSIONS_UPDATE_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              parent_lookup_user: userId,
              id: permissionSettingId,
            },
          },
          body: {
            data: {
              type: 'UserDepartmentPermission',
              id: permissionSettingId,
              relationships: {
                permission_group: {
                  data: { type: 'PermissionGroup', id: newPermissionId },
                },
                department: {
                  data: { type: 'Department', id: depId },
                },
              },
            },
          },
        }),
      )
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['locumUser', variables.userId],
      })
      await queryClient.invalidateQueries({ queryKey: ['locum-users-list'] })
      await queryClient.invalidateQueries({
        queryKey: ['clinic-staff-list', route.params.provetId],
      })
      await queryClient.invalidateQueries({
        queryKey: ['clinicStaffUsers'],
      })
    },
  })
}

const userRelationships = {
  active_department: one('Department'),
  home_department: one('Department'),
  available_departments: many('Department'),
  available_department_groups: many('DepartmentGroup'),
  permission_group: one('PermissionGroup'),
} satisfies RelationshipDefs<Schemas['User']>

export const buildUserRelationships =
  createRelationshipBuilder(userRelationships)

const departmentRelationships = {
  feature_blocks: many('FeatureBlock'),
} satisfies RelationshipDefs<Schemas['Department']>

export const buildDepartmentRelationships = createRelationshipBuilder(
  departmentRelationships,
)

const mergedSettingsRelationships = {
  active_department: one('Department'),
  home_department: one('Department'),
} satisfies RelationshipDefs<Schemas['MergedSettings']>

export const buildMergedSettingsRelationships = createRelationshipBuilder(
  mergedSettingsRelationships,
)
